import { messages, type LocaleCode, type MessageKey } from '../locales/messages';

const STORAGE_KEY = 'jgi_locale';

function tableOf(code: LocaleCode) {
  return (messages[code] || messages.id) as Record<string, string>;
}

function msgOf(code: LocaleCode, key: string) {
  return tableOf(code)[key] || tableOf('id')[key] || key;
}

function applyLocaleDom(code: LocaleCode) {
  if (!import.meta.client) return;
  document.documentElement.lang = code === 'en' ? 'en' : 'id';
  document.documentElement.dataset.locale = code;
}

export function useI18n() {
  const locale = useState<LocaleCode>('app-locale', () => 'id');
  const { runSwitch } = useAppSwitchOverlay();

  function load() {
    if (!import.meta.client) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'id' || raw === 'en') locale.value = raw;
    applyLocaleDom(locale.value);
  }

  function t(key: MessageKey | string, params?: Record<string, string | number>) {
    void locale.value;
    let text = msgOf(locale.value, String(key));
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return text;
  }

  function setLocale(next: LocaleCode, opts?: { silent?: boolean }) {
    if (next === locale.value) return;

    const commit = () => {
      locale.value = next;
      if (import.meta.client) {
        localStorage.setItem(STORAGE_KEY, next);
        applyLocaleDom(next);
        window.dispatchEvent(
          new CustomEvent('jgi-locale-changed', { detail: { locale: next } })
        );
      }
    };

    if (opts?.silent || !import.meta.client) {
      commit();
      return;
    }

    // Teks overlay memakai bahasa TUJUAN supaya langsung terasa sinkron
    const title = msgOf(next, 'switch.localeTitle');
    const toLine = msgOf(next, next === 'en' ? 'switch.localeToEn' : 'switch.localeToId');
    const subHint = msgOf(next, 'switch.localeSub');

    void runSwitch({
      kind: 'locale',
      title,
      subtitle: `${toLine} · ${subHint}`,
      steps: [
        { key: 'ui', label: msgOf(next, 'switch.localeStepUi') },
        { key: 'login', label: msgOf(next, 'switch.localeStepLogin') },
        { key: 'app', label: msgOf(next, 'switch.localeStepApp') },
      ],
      apply: commit,
    });
  }

  function tQualityLabel(raw?: string | null) {
    const s = String(raw || '').trim();
    if (!s) return '—';
    if (/belum siap spt/i.test(s)) return t('quality.notReadySpt');
    if (/siap review/i.test(s)) return t('quality.readyReview');
    if (/perlu lengkapi/i.test(s)) return t('quality.needComplete');
    if (/not ready for tax/i.test(s)) return t('quality.notReadySpt');
    if (/ready for consultant/i.test(s)) return t('quality.readyReview');
    if (/needs completion/i.test(s)) return t('quality.needComplete');
    return s;
  }

  function tYearSubtitle(raw?: string | null, isCurrent?: boolean) {
    const s = String(raw || '').trim();
    if (isCurrent || /tahun ini|this year/i.test(s)) return t('dash.thisYear');
    if (/ada data omset|has revenue/i.test(s)) return t('dash.hasOmsetData');
    if (s) return s;
    return t('dash.yearAvailable');
  }

  return { locale, t, setLocale, load, tQualityLabel, tYearSubtitle, msgOf };
}
